import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { Befehlsnachricht } from '@/models/Befehlsnachricht'
import { Abruftoken } from '@/models/Abruftoken'
import { createStorage } from './storage'
import { convertStandortToDto, createBefehlsnachrichtHash } from '@repo/ui'
import { decodeJwt } from 'jose'
import { LegacyBfnStoreDataEntryV2 } from './legacyTypes'

/**
 * If the stored state's version mismatch the one specified here, the storage will not be used.
 * Change this version when adding a breaking change.
 */
const BFN_STORE_VERSION = 3

export interface BfnStoreDataEntry {
  abruftoken: Abruftoken
  befehlsnachricht: Befehlsnachricht
  standortSentDate?: Date
  manuellWiderrufenAt?: Date
}

export type BfnStoreData = Record<string, BfnStoreDataEntry>

export interface BfnStoreState {
  pendingTokens: Abruftoken[]
  data: BfnStoreData
}

export interface BfnStoreActions {
  addPendingToken: (abruftoken: Abruftoken) => void
  removePendingToken: (abruftoken: Abruftoken) => void
  addData: (id: string, abruftoken: Abruftoken, bfn: Befehlsnachricht) => void
  updateBefehlsnachricht: (id: string, bfn: Befehlsnachricht) => void
  setStandortSentDate: (id: string, date: Date) => void
  removeData: (id: string) => void
  setManuellWiderrufenAt: (id: string, value: Date | undefined) => void
}

export const initialState: BfnStoreState = {
  pendingTokens: [],
  data: {}
}

export const migrateFunction = async (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  persistedState: any,
  version: number
): Promise<BfnStoreState> => {
  if (version < 2) {
    return initialState
  }
  if (version == 2) {
    const updatedData = { ...persistedState.data }

    // Loop über alle IDs im data-Objekt
    await Promise.all(
      Object.keys(updatedData).map(async (id) => {
        const oldDataEntry = persistedState.data[
          id
        ] as LegacyBfnStoreDataEntryV2

        const dataEntry = updatedData[id]

        const jwtPayload = decodeJwt(dataEntry.abruftoken.abruftoken)
        const newBearbeitbarBis = jwtPayload.exp
          ? new Date(jwtPayload.exp * 1000)
          : null

        dataEntry.befehlsnachricht = {
          ...dataEntry.befehlsnachricht,
          bearbeitbarBis: newBearbeitbarBis,
          eindeutigeKennungHash: await createBefehlsnachrichtHash({
            id: oldDataEntry.befehlsnachricht.id,
            eindeutigeKennung: oldDataEntry.befehlsnachricht.eindeutigeKennung
          }),
          statusTfHash: await createBefehlsnachrichtHash({
            id: oldDataEntry.befehlsnachricht.id,
            visuellerStatus: oldDataEntry.befehlsnachricht.visuellerStatus,
            technischerStatus: oldDataEntry.befehlsnachricht.status,
            statusTimestamps:
              oldDataEntry.befehlsnachricht.statusTimestamps.map((data) => {
                return {
                  status: data.status,
                  time: data.time.toISOString(),
                  visuellerStatus: data.visuellerStatus
                }
              })
          }),
          standortHash: oldDataEntry.befehlsnachricht.standort
            ? await createBefehlsnachrichtHash({
                id: oldDataEntry.befehlsnachricht.id,
                standort: convertStandortToDto(
                  oldDataEntry.befehlsnachricht.standort
                )
              })
            : null
        }
      })
    )

    return {
      ...persistedState,
      data: updatedData
    }
  }

  return persistedState
}
// noinspection JSUnusedGlobalSymbols
export const useBfnStore = create<BfnStoreState & BfnStoreActions>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,
        addPendingToken: (abruftoken: Abruftoken) =>
          set((state) => ({
            pendingTokens: [...state.pendingTokens, abruftoken]
          })),
        removePendingToken: (tokenToRemove: Abruftoken) =>
          set((state) => ({
            pendingTokens: state.pendingTokens.filter(
              (abruftoken) => abruftoken !== tokenToRemove
            )
          })),
        addData: (id: string, abruftoken: Abruftoken, bfn: Befehlsnachricht) =>
          set((state) => {
            if (state.data[id]) {
              throw new Error(
                `Store-Fehler: Befehlsnachricht existiert bereits. ID: "${id}"`
              )
            }
            return {
              data: {
                ...state.data,
                [id]: {
                  abruftoken,
                  befehlsnachricht: bfn,
                  manuellWiderrufenAt: undefined
                }
              }
            }
          }),
        updateBefehlsnachricht: (id: string, bfn: Befehlsnachricht) =>
          set((state) => {
            if (!state.data[id]) {
              throw new Error(
                `Store-Fehler: Keine Befehlsnachricht zum Aktualisieren gefunden. ID: "${id}"`
              )
            }
            return {
              data: {
                ...state.data,
                [id]: { ...state.data[id], befehlsnachricht: bfn }
              }
            }
          }),
        setStandortSentDate: (id: string, date: Date) =>
          set((state) => {
            if (!state.data[id]) {
              throw new Error(
                `Store-Fehler: Keine Befehlsnachricht zum Setzen des Standort-Absendedatums gefunden. ID: "${id}"`
              )
            }
            return {
              data: {
                ...state.data,
                [id]: {
                  ...state.data[id],
                  standortSentDate: date
                }
              }
            }
          }),
        setManuellWiderrufenAt: (id: string, value: Date | undefined) => {
          set((state) => {
            if (!state.data[id]) {
              throw new Error(
                `Store-Fehler: Keine Befehlsnachricht zum 'manuell widerrufen' gefunden. ID: "${id}"`
              )
            }

            return {
              ...state,
              data: {
                ...state.data,
                [id]: {
                  ...state.data[id],
                  manuellWiderrufenAt: value
                }
              }
            }
          })
        },
        removeData: (id: string) =>
          set((state) => {
            const updatedData = { ...state.data }
            delete updatedData[id]
            return { data: updatedData }
          })
      }),
      {
        name: 'bfn-storage',
        storage: createStorage(),
        version: BFN_STORE_VERSION,
        migrate: migrateFunction
      }
    )
  )
)
